import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import SubjectProgressCircle from '../components/subject/SubjectProgressCircle';
import SubjectTable from '../components/subject/SubjectTable';
import SubjectModal from '../components/subject/SubjectModal';

import {
  useGetAllSubjects,
  useDeleteSubject,
} from '../api/subject/subjectQueries';
import { useGetDepartments } from '../api/department/departmentQueries';

export default function Subject() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subjectToEdit, setSubjectToEdit] = useState(null);

  // Fetch departments for dropdown
  const { data: departments = [] } = useGetDepartments();

  // Fetch subjects
  const { data: rawSubjects = [], isLoading: isLoadingSubjects } =
    useGetAllSubjects();

  // Delete subject hook
  const { mutate: deleteSubject } = useDeleteSubject();

  // Transform subjects for the table display
  const subjects = useMemo(() => {
    const subjectsArray = Array.isArray(rawSubjects)
      ? rawSubjects
      : Array.isArray(rawSubjects.data)
      ? rawSubjects.data
      : [];

    return subjectsArray.map((subject) => ({
      id: subject.id || subject.subjectId,
      name: subject.subjectName || subject.name,
      code: subject.code || subject.subjectCode || '',
      department: subject.department || {
        id: subject.departmentId,
        name:
          subject.departmentName ||
          (subject.department ? subject.department.name : 'N/A'),
      },
      enrolled: subject.enrolled || subject.studentCount || 0,
      createdAt: subject.createdAt || subject.created_at,
      status: subject.status || 'Active',
    }));
  }, [rawSubjects]);

  // Calculate subject progress data
  const subjectProgress = useMemo(() => {
    const subjectsArray = Array.isArray(rawSubjects)
      ? rawSubjects
      : Array.isArray(rawSubjects.data)
      ? rawSubjects.data
      : [];

    return subjectsArray.slice(0, 4).map((subject) => {
      // Show top 4 subjects
      // Calculate progress percentage based on various metrics
      let totalScore = 0;
      let metrics = 0;

      // If we have student performance data
      if (subject.averagePerformance) {
        totalScore += subject.averagePerformance;
        metrics++;
      }

      // If we have attendance data
      if (subject.attendanceRate) {
        totalScore += subject.attendanceRate;
        metrics++;
      }

      // If we have completion data
      if (subject.completionRate) {
        totalScore += subject.completionRate;
        metrics++;
      }

      // Calculate final percentage with fallback
      const percentage =
        metrics > 0
          ? Math.round(totalScore / metrics)
          : Math.round(Math.random() * (85 - 60) + 60); // Fallback between 60-85%

      // Determine color based on percentage
      let color;
      if (percentage >= 80) color = 'green';
      else if (percentage >= 60) color = 'purple';
      else color = 'red';

      return {
        subject: subject.subjectName || subject.name,
        percentage,
        color,
      };
    });
  }, [rawSubjects]);

  // Handle subject deletion
  const handleDeleteSubject = (subjectId) => {
    deleteSubject(subjectId, {
      onSuccess: () => {
        toast.success('Subject deleted successfully');
      },
      onError: (error) => {
        toast.error(
          `Failed to delete subject: ${error.message || 'Unknown error'}`
        );
      },
    });
  };

  // Handle opening modal for creating or editing
  const handleOpenModal = (subject = null) => {
    if (subject) {
      // Transform the subject data to match what the API expects
      const transformedSubject = {
        id: subject.id,
        subjectId: subject.id,
        subjectName: subject.name,
        departmentId: subject.department?.id || subject.departmentId,
      };
      console.log('Opening edit modal with subject:', transformedSubject);
      setSubjectToEdit(transformedSubject);
    } else {
      setSubjectToEdit(null);
    }
    setIsModalOpen(true);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSubjectToEdit(null);
  };

  return (
    <div className="w-full px-4 py-6 mx-auto">
      {/* Subject Progress */}
      <div className="mb-8">
        <h2 className="text-lg font-medium mb-4">Subject Progress</h2>
        <div className="p-6 bg-white rounded-lg shadow">
          <div className="flex flex-wrap gap-6 justify-start">
            {subjectProgress.map((item, index) => (
              <SubjectProgressCircle
                key={index}
                subject={item.subject}
                percentage={item.percentage}
                color={item.color}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Subject List */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Subject List</h2>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            onClick={() => handleOpenModal()}
          >
            Add Subject
          </button>
        </div>
        <div className="bg-white rounded-lg shadow">
          <SubjectTable
            subjects={subjects}
            isLoading={isLoadingSubjects}
            onEditSubject={handleOpenModal}
            onDeleteSubject={handleDeleteSubject}
          />
        </div>
      </div>

      {/* Subject Modal */}
      <SubjectModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        departments={departments}
        subjectToEdit={subjectToEdit}
      />
    </div>
  );
}
